// @ts-nocheck

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { formatCount, formatPercentage } from "@/lib/utils/format";
import { Spinner } from "@/components/ui/Spinner";
import { calculateGrowthRate } from "@/lib/services/analytics/analyticsService";
import { cn } from "@/lib/utils/cn";

interface PlatformStats {
  total_users: number;
  new_users_today: number;
  new_users_week: number;
  total_posts: number;
  new_posts_today: number;
  total_comments: number;
  pending_reports: number;
  total_reports: number;
  banned_users: number;
  verified_users: number;
}

export function AdminAnalyticsClient() {
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("7d");

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/admin/stats?period=${period}`);
        const data = await res.json();
        setStats(data);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [period]);

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;
  if (!stats) return null;

  const statCards = [
    { label: "Total Users", value: stats.total_users, sub: `+${stats.new_users_today} today`, color: "text-brand" },
    { label: "New This Week", value: stats.new_users_week, sub: "new registrations", color: "text-accent" },
    { label: "Total Posts", value: stats.total_posts, sub: `+${stats.new_posts_today} today`, color: "text-success" },
    { label: "Total Comments", value: stats.total_comments, sub: "published", color: "text-brand" },
    { label: "Pending Reports", value: stats.pending_reports, sub: `${stats.total_reports} total`, color: stats.pending_reports > 10 ? "text-destructive" : "text-warning" },
    { label: "Banned Users", value: stats.banned_users, sub: `${stats.verified_users} verified`, color: "text-destructive" },
  ];

  return (
    <div className="space-y-8">
      {/* Period selector */}
      <div className="flex gap-2">
        {["1d", "7d", "30d"].map(p => (
          <button key={p} onClick={() => setPeriod(p)}
            className={cn("px-4 py-2 rounded-xl text-sm font-medium transition-colors",
              period === p ? "bg-brand text-white" : "bg-muted text-text-muted hover:bg-muted/80"
            )}>
            {p === "1d" ? "24 Hours" : p === "7d" ? "7 Days" : "30 Days"}
          </button>
        ))}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((card, i) => (
          <motion.div key={card.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }} className="card p-4 space-y-1">
            <p className="text-xs text-text-muted font-medium">{card.label}</p>
            <p className={cn("text-2xl font-bold tabular-nums", card.color)}>
              {formatCount(card.value)}
            </p>
            <p className="text-xs text-text-subtle">{card.sub}</p>
          </motion.div>
        ))}
      </div>

      {/* Health indicators */}
      <div className="card p-6">
        <h2 className="text-base font-semibold text-foreground mb-4">Platform Health</h2>
        <div className="space-y-4">
          {[
            {
              label: "Report resolution rate",
              value: stats.total_reports > 0
                ? ((stats.total_reports - stats.pending_reports) / stats.total_reports) * 100
                : 100,
              good: (v: number) => v > 80,
            },
            {
              label: "Verified users rate",
              value: stats.total_users > 0
                ? (stats.verified_users / stats.total_users) * 100
                : 0,
              good: (v: number) => v > 0.5,
            },
            {
              label: "Banned users rate",
              value: stats.total_users > 0
                ? (stats.banned_users / stats.total_users) * 100
                : 0,
              good: (v: number) => v < 5,
            },
          ].map(metric => (
            <div key={metric.label} className="flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-foreground">{metric.label}</span>
                  <span className={cn("text-sm font-bold",
                    metric.good(metric.value) ? "text-success" : "text-warning"
                  )}>
                    {formatPercentage(metric.value)}
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(metric.value, 100)}%` }}
                    transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                    className={cn("h-full rounded-full",
                      metric.good(metric.value) ? "bg-success" : "bg-warning"
                    )}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
